import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { hashStringToSeed } from './seededRandom';
import { workerAppearance } from './workerAppearance';
import { workerUniform } from './workerUniforms';
import { workerRoamPath } from './workerRoamPath';
import { WorkerModel } from './WorkerModel';

const MOVE_SPEED = 1.2;       // units/sec lerp toward target
const BOB_AMP = 0.04;
const STRIDE_FREQ = 8;        // leg swing frequency while walking

/**
 * Animated modular worker. Owns the only useFrame: roam target -> lerp -> idle/walk transforms.
 * Leg swing is shared with WorkerModel through a mutable motionRef (no per-frame re-render).
 * @param {object} worker - { id, type, assignedRoomId, ... }
 * @param {[number,number,number]} yardPos - the worker's yard idle slot
 * @param {?[number,number,number]} roomDoorPos - assigned room door, or null when unassigned
 */
export function WorkerSprite({ worker, yardPos, roomDoorPos }) {
  const groupRef = useRef();
  const motionRef = useRef({ legSwing: 0 });
  const pos = useRef([...yardPos]);
  const seed = useMemo(() => hashStringToSeed(worker.id), [worker.id]);
  const appearance = useMemo(() => workerAppearance(worker.id), [worker.id]);
  const uniform = useMemo(() => workerUniform(worker.type), [worker.type]);

  useFrame((state, delta) => {
    const { target, phase, visible } = workerRoamPath({
      elapsed: state.clock.elapsedTime, seed, yardPos, roomDoorPos,
    });

    const prevX = pos.current[0], prevZ = pos.current[2];
    const lerp = Math.min(1, MOVE_SPEED * delta);
    pos.current[0] += (target[0] - prevX) * lerp;
    pos.current[2] += (target[2] - prevZ) * lerp;

    const g = groupRef.current;
    if (!g) return;
    g.visible = visible;
    if (!visible) { motionRef.current.legSwing = 0; return; }

    const walking = phase === 'walking';
    const bob = walking
      ? Math.abs(Math.sin(state.clock.elapsedTime * STRIDE_FREQ)) * BOB_AMP
      : Math.sin(state.clock.elapsedTime * 2 + seed) * (BOB_AMP * 0.5);
    g.position.set(pos.current[0], yardPos[1] + bob, pos.current[2]);

    // Face direction of travel while moving
    const dx = pos.current[0] - prevX, dz = pos.current[2] - prevZ;
    if (walking && (Math.abs(dx) > 1e-4 || Math.abs(dz) > 1e-4)) {
      g.rotation.y = Math.atan2(dx, dz);
    }

    // WorkerModel reads this each frame to rotate the leg groups imperatively.
    motionRef.current.legSwing = walking ? Math.sin(state.clock.elapsedTime * STRIDE_FREQ) * 0.5 : 0;
  });

  return (
    <group ref={groupRef} position={yardPos}>
      <WorkerModel appearance={appearance} uniform={uniform} motionRef={motionRef} />
    </group>
  );
}
